import { useState } from "react";
import { ChevronDown, ChevronLeft, Network } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { EmptyState } from "@/components/shared/EmptyState";
import { cn } from "@/lib/utils";
import type { DepartmentTreeNode } from "@/types/department";
import { useDepartmentPermissions } from "@/hooks/useDepartmentPermissions";
import { useUpdateDepartmentSettings } from "@/features/departments/hooks/useDepartmentsMutations";

interface DepartmentTreeProps {
  nodes: DepartmentTreeNode[];
  selectedId?: string;
  onSelect: (department: DepartmentTreeNode) => void;
  /** When true, all branches stay expanded (e.g. while filters are active). */
  forceExpanded?: boolean;
  hasActiveFilters?: boolean;
  level?: number;
}

function DepartmentTreeNodeItem({
  node,
  selectedId,
  onSelect,
  forceExpanded,
  level = 0,
}: {
  node: DepartmentTreeNode;
  selectedId?: string;
  onSelect: (department: DepartmentTreeNode) => void;
  forceExpanded?: boolean;
  level?: number;
}) {
  const [expanded, setExpanded] = useState(true);
  const { canManage } = useDepartmentPermissions();
  const updateSettings = useUpdateDepartmentSettings();
  const hasChildren = (node.children?.length ?? 0) > 0;
  const isSelected = selectedId === node.id;
  const isExpanded = forceExpanded ? true : expanded;

  const handleToggleActive = (checked: boolean) => {
    updateSettings.mutate({
      id: node.id,
      data: { isActive: checked },
    });
  };

  return (
    <div>
      <div
        className={cn(
          "flex items-center gap-2 rounded-lg border px-3 py-2 mb-1 transition-colors",
          isSelected
            ? "border-primary bg-primary/5"
            : "border-border hover:bg-muted",
        )}
        style={{ marginRight: `${level * 16}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={isExpanded ? "طي" : "توسيع"}
          >
            {isExpanded ? (
              <ChevronDown className="size-4" />
            ) : (
              <ChevronLeft className="size-4" />
            )}
          </button>
        ) : (
          <span className="size-4 shrink-0" />
        )}

        <button
          type="button"
          onClick={() => onSelect(node)}
          className="flex-1 text-right min-w-0"
        >
          <div className="font-medium text-sm truncate">{node.nameAr}</div>
          <div className="text-xs text-muted-foreground font-mono">
            {node.code}
          </div>
        </button>

        <div className="flex items-center gap-1 shrink-0">
          <Badge variant={node.isActive ? "default" : "secondary"}>
            {node.isActive ? "نشط" : "غير نشط"}
          </Badge>
          <Badge variant="outline">
            {node.requiresApproval ? "يتطلب موافقة" : "نشر مباشر"}
          </Badge>
        </div>

        {canManage && (
          <Switch
            checked={node.isActive}
            onCheckedChange={handleToggleActive}
            disabled={updateSettings.isPending}
            aria-label={`تفعيل ${node.nameAr}`}
          />
        )}
      </div>

      {hasChildren && isExpanded && (
        <DepartmentTree
          nodes={node.children ?? []}
          selectedId={selectedId}
          onSelect={onSelect}
          forceExpanded={forceExpanded}
          level={level + 1}
        />
      )}
    </div>
  );
}

export function DepartmentTree({
  nodes,
  selectedId,
  onSelect,
  forceExpanded,
  hasActiveFilters,
  level = 0,
}: DepartmentTreeProps) {
  if (nodes.length === 0) {
    return (
      <EmptyState
        icon={Network}
        title={hasActiveFilters ? "لا توجد نتائج مطابقة" : "لا توجد دوائر"}
        description={
          hasActiveFilters ? "جرّب تعديل معايير البحث أو الفلاتر." : undefined
        }
      />
    );
  }

  return (
    <div className="space-y-1">
      {nodes.map((node) => (
        <DepartmentTreeNodeItem
          key={node.id}
          node={node}
          selectedId={selectedId}
          onSelect={onSelect}
          forceExpanded={forceExpanded}
          level={level}
        />
      ))}
    </div>
  );
}
